/**
 * Recognises the "the wallet needs to be re-authenticated" family of errors and
 * gives them a message a user can act on.
 *
 * Where these come from. `@cef-ai/wallet` keeps signing keys in memory only —
 * `resume()` restores the bearer session but never the PRF-derived keys, so
 * `hasKeys` is false after every reload. A raw signature then triggers a
 * step-up ceremony whose popup must be opened synchronously inside a user
 * gesture; without one the browser blocks it and the SDK raises
 * `popup-blocked`. Surfacing that string verbatim tells the user "popup window
 * failed to open", which is both mystifying and misleading: nothing is wrong
 * with their popup blocker, their session simply needs re-authenticating.
 *
 * Detection is STRUCTURAL (on `code`) rather than `instanceof WalletError`:
 * `@cef-ai/wallet` does not export the error class, and even if it did, an
 * `instanceof` across duplicated module instances is a silent-failure trap.
 */

/**
 * Error codes that all mean the same thing to a user: they have to act from a
 * fresh click before the wallet can sign.
 *
 * `gesture-required` is the one that matters now (@cef-ai/wallet 3.0.0). When a
 * ceremony would need a popup and no transient activation is left, the wallet
 * raises this INSTEAD of calling `window.open` and having the browser swallow
 * it. That makes the condition deterministic; `popup-blocked` depended on the
 * browser's popup-blocker heuristics and is kept because older wallet versions
 * and the popup transport still raise it.
 *
 * The remedy is identical for every code here — re-run the operation from a
 * fresh gesture — which is what makes one shared message honest. Note vault
 * re-runs the whole operation rather than retrying the failed call: there is no
 * resume point, and none is needed, because the claim is idempotent end to end
 * (see `lib/clients/vault/claim.ts`). An app whose operation is NOT idempotent
 * cannot borrow that reasoning.
 *
 * Adding a code here can only improve a message; the one place it also fed
 * control flow was the removed `delegateGrant.needsStepUp` branch, where
 * "offer a retry" was the correct branch for every one of these — a pattern
 * worth copying if a per-step outcome ever needs distinguishing again.
 */
const STEP_UP_CODES = new Set(['popup-blocked', 'gesture-required', 'needs-step-up', 'unauthorized']);

export const RECONNECT_MESSAGE = 'Your wallet session needs to be reconnected to continue.';

/**
 * The error to raise when WE decide the wallet is not usable for signing —
 * currently, when `wallet.ceremonySurface()` reports `'popup'` — a signature
 * from here would need a `window.open` the browser will block.
 *
 * A factory rather than an inline literal because it is thrown from two places
 * that must stay identical: the ceremony-surface gate in
 * `src/routes/vault-layout.tsx` and the one inside `lib/clients/vault/claim.ts`.
 * Divergence between them would mean the same condition rendered two different
 * messages depending on which gate caught it.
 *
 * The `code` is what makes {@link isStepUpRequiredError} recognise it, so
 * {@link toDisplayError} yields the actionable reconnect line instead of showing
 * the raw string. This is the one error in this module we CONSTRUCT; everything
 * else here only classifies errors raised by the wallet.
 */
export function stepUpRequiredError(): Error {
  return Object.assign(new Error(RECONNECT_MESSAGE), { code: 'needs-step-up' });
}

function codeOf(e: unknown): string | undefined {
  if (typeof e !== 'object' || e === null) return undefined;
  const code = (e as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

/** True when `e` means "re-authenticate", rather than a genuine failure. */
export function isStepUpRequiredError(e: unknown): boolean {
  const code = codeOf(e);
  if (code && STEP_UP_CODES.has(code)) return true;
  // Fallback for transports that flatten the error to a bare `Error`. Narrow
  // on purpose — matching loosely here would relabel unrelated failures as
  // "reconnect" and hide real bugs behind a retry button.
  return e instanceof Error && /popup[- ]blocked|gesture[- ]required|needs[- ]step[- ]up/i.test(e.message);
}

/**
 * The message to show for `e`: the actionable reconnect line for a step-up
 * error, otherwise the error's own message unchanged. Deliberately does NOT
 * rewrite anything else — a swallowed root cause is worse than an ugly string.
 */
export function toDisplayError(e: unknown): string {
  if (isStepUpRequiredError(e)) return RECONNECT_MESSAGE;
  return e instanceof Error ? e.message : String(e);
}
